import { Box, IconButton } from "@mui/material"
import { MoreVertOutlined } from "@mui-symbols-material/w300"
import type { Row } from "@tanstack/react-table"
import { type ReactElement, useRef, useState } from "react"

import type { Instruction } from "@/graphql/codegen/graphql"
import {
  DeactivateInstructionWarning,
  RemoveProductDialog,
} from "@/screens/ManageProducts/components/Instructions/components/dialogs"
import InstructionDropdownMenu from "@/screens/ManageProducts/components/Instructions/components/rowActions/InstructionDropdownMenu"
import useInstructionActions from "@/screens/ManageProducts/components/Instructions/hooks/useInstructionActions"
import useProductsByInstructionList from "@/screens/ManageProducts/components/Instructions/hooks/useProductsByInstructionList"
import useSearchList from "@/screens/ManageProducts/components/Instructions/hooks/useSearchList"

interface InstructionActionsProps {
  row: Row<Instruction>
  onEditInstruction: (instruction: Instruction) => void
  refetchProductsList: (id: number) => void
}

export const InstructionActions = ({
  row,
  onEditInstruction,
  refetchProductsList,
}: InstructionActionsProps): ReactElement => {
  const instruction = row.original
  const instructionId = instruction.instructionId
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const { refetchData } = useSearchList()
  const { removeProductsFromInstruction, updateInstructionStatus } = useInstructionActions()

  const [open, setOpen] = useState<boolean>(false)

  const handleOpen = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }

  const [isProductsAssociatedWarningOpened, setIsProductsAssociatedWarningOpened] = useState(false)
  const [isRemoveDialogOpened, setIsRemoveDialogOpened] = useState(false)

  const { products, fetchingProducts } = useProductsByInstructionList({
    instructionId,
    pause: !open,
  })
  const hasProductsAssociatedWithInstruction = !!products.length

  const changeStatus = async (nextActiveStatus: boolean) => {
    await updateInstructionStatus({
      instructionId,
      isActive: nextActiveStatus,
    })
    refetchData()
  }

  const onToggleActive = (): void => {
    const nextActiveStatus = !instruction.isActive
    if (!nextActiveStatus && hasProductsAssociatedWithInstruction) {
      setIsProductsAssociatedWarningOpened(true)
      return
    }
    changeStatus(nextActiveStatus)
  }

  const removeProductsAssociation = async (productIds: number[]): Promise<void> => {
    const success = await removeProductsFromInstruction({
      instructionId,
      productIds,
    })
    if (success) {
      setIsRemoveDialogOpened(false)
      refetchProductsList(Number(row.id))
    }
  }

  return (
    <Box className='flex items-center justify-end space-x-4'>
      <Box className='flex items-center justify-end' ref={anchorElRef}>
        <IconButton onClick={handleOpen}>
          <MoreVertOutlined />
        </IconButton>
        <InstructionDropdownMenu
          hasProducts={hasProductsAssociatedWithInstruction}
          onEditInstruction={onEditInstruction}
          onRemoveProducts={() => setIsRemoveDialogOpened(true)}
          fetchingProducts={fetchingProducts}
          instruction={instruction}
          onToggleActive={onToggleActive}
          open={open}
          anchorEl={anchorElRef.current}
          onClose={handleClose}
        />
      </Box>
      {isProductsAssociatedWarningOpened && (
        <DeactivateInstructionWarning
          products={products}
          productsFetching={fetchingProducts}
          onClose={() => setIsProductsAssociatedWarningOpened(false)}
          onSubmit={() => changeStatus(false)}
        />
      )}
      {isRemoveDialogOpened && (
        <RemoveProductDialog
          products={products}
          productsFetching={fetchingProducts}
          onClose={() => setIsRemoveDialogOpened(false)}
          onSubmit={removeProductsAssociation}
        />
      )}
    </Box>
  )
}
